/**
 * Breadcrumbs for the advertising objects, built from the object itself rather
 * than from the route that reached it: an advertiser owns orders, an order owns
 * line items, a line item owns creatives, and the trail names that chain —
 * Advertiser › Order › Line item › Creative — cut short at whatever page you
 * are on.
 */

/** One ancestor, as the object being edited names it. */
export interface CrumbRef {
  id?: string | null
  name?: string | null
}

export interface Crumb {
  label: string
  href?: string
}

/** The ancestors of the object on the page, in the order they nest. */
export interface ObjectAncestors {
  advertiser?: CrumbRef
  order?: CrumbRef
  lineItem?: CrumbRef
}

export const OBJECT_HREFS = {
  advertiser: (id: string) => `/advertising/advertisers/${id}`,
  order: (id: string) => `/advertising/orders/${id}`,
  lineItem: (id: string) => `/advertising/lineitems/${id}`,
} as const

/**
 * The trail for a page: its object's ancestors, then the object itself as the
 * unlinked last crumb.
 *
 * An ancestor that is not loaded yet, or that the object does not have, is left
 * out rather than shown as a gap — the trail shortens instead of flickering.
 */
export function objectCrumbs(ancestors: ObjectAncestors, currentLabel: string): Crumb[] {
  const crumbs: Crumb[] = []
  for (const kind of ['advertiser', 'order', 'lineItem'] as const) {
    const ref = ancestors[kind]
    if (ref?.id && ref.name) crumbs.push({ label: ref.name, href: OBJECT_HREFS[kind](ref.id) })
  }
  crumbs.push({ label: currentLabel })
  return crumbs
}
